"use client"

import { Bell, Users, Moon, Sun, ChevronLeft } from "lucide-react"
import { useAuth } from "@/lib/auth/context"
import { useRouter, usePathname } from "next/navigation"
import { useState, useEffect, useCallback } from "react"
import { createClient } from "@/lib/supabase/client"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import Image from "next/image"
import { Button } from "@/components/ui/button"
import { useTheme } from "next-themes"
import { usePlatform, triggerHaptic } from "@/hooks/use-platform"

export function MobileAppHeader() {
  const { user, loading } = useAuth()
  const router = useRouter()
  const pathname = usePathname()
  const { theme, setTheme } = useTheme()
  const { isMobile, isNative, isStandalone, isIOS } = usePlatform()
  const [mounted, setMounted] = useState(false)
  const [userProfile, setUserProfile] = useState<any>(null)
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0)
  const [unreadMessageCount, setUnreadMessageCount] = useState(0)
  const [completionPercentage, setCompletionPercentage] = useState(0)

  // App mode logic consistent with root pages
  const isAppMode = isMobile && (
    isNative || 
    (isStandalone && !isIOS)
  )

  const isProfilePage = pathname === "/profile" || pathname.startsWith("/user/")
  const isNotificationPage = pathname === "/notifications"
  const isNotificationCenterPage = pathname === "/settings/notifications"
  const isSettingsPage = pathname === "/settings"
  const isSpotPage = pathname.startsWith("/spot/")
  const isSubPage = isProfilePage || isNotificationPage || isSpotPage || isSettingsPage || isNotificationCenterPage

  // Center Content
  const getSubPageTitle = () => {
    if (isProfilePage) return "Profile"
    if (isNotificationPage) return "Notifications"
    if (isNotificationCenterPage) return "Notification Center"
    if (isSettingsPage) return "Settings"
    if (isSpotPage) return "Spot Details"
    return ""
  }

  // Calculate profile completeness
  const calculateCompleteness = useCallback((profile: any) => {
    if (!profile) return 0
    const fields = [
      'first_name',
      'last_name',
      'avatar_url',
      'location',
      'surf_experience',
      'kitesurfing_experience',
      'bio',
      'instagram_handle',
      'date_of_birth',
      'gender'
    ]
    const completedFields = fields.filter(field => {
      const value = profile[field]
      return value !== null && value !== undefined && value !== '' && value !== 'no_experience'
    })
    return Math.round((completedFields.length / fields.length) * 100)
  }, [])

  // Fix hydration issues
  useEffect(() => {
    setMounted(true)
  }, [])

  const toggleTheme = () => {
    triggerHaptic('impact')
    setTheme(theme === "dark" ? "light" : "dark")
  }

  const handleBack = () => {
    triggerHaptic('impact')
    
    // Check if we can go back, otherwise go home
    if (typeof window !== 'undefined' && window.history.length > 1) {
      router.back()
    } else {
      router.push('/home')
    }
  }

  const fetchUserProfile = useCallback(async () => {
    if (!user) return
    try {
      const supabase = createClient()
      const { data, error } = await supabase
        .from("users")
        .select("avatar_url, name, first_name, last_name, id, location, surf_experience, kitesurfing_experience, bio, instagram_handle, date_of_birth, gender")
        .eq("auth_id", user.id)
        .single()
      
      if (!error && data) {
        setUserProfile(data)
        setCompletionPercentage(calculateCompleteness(data))
      }
    } catch (error) {
      console.error('Failed to fetch user profile:', error)
    }
  }, [user, calculateCompleteness])

  const fetchUnreadCounts = useCallback(async () => {
    if (!user?.id) return
    try {
      const supabase = createClient()
      
      // Messages count
      const { count: msgCount } = await supabase
        .from("messages")
        .select("id", { count: "exact", head: true })
        .eq("recipient_id", user.id)
        .neq("is_read", true)
      
      setUnreadMessageCount(msgCount || 0)

      // Notifications count
      const { data: profile } = await supabase
        .from("users")
        .select("id")
        .eq("auth_id", user.id)
        .single()

      if (profile?.id) {
        const { count: notifCount } = await supabase
          .from("notifications")
          .select("id", { count: "exact", head: true })
          .eq("user_id", profile.id)
          .eq("is_read", false)
          .neq("type", "chat_message")
        
        setUnreadNotificationCount(notifCount || 0)
      }
    } catch (error) {
      console.error("Error fetching unread counts:", error)
    }
  }, [user])

  useEffect(() => {
    if (user && !loading) {
      fetchUserProfile()
      fetchUnreadCounts()

      const supabase = createClient()
      const channel = supabase
        .channel("mobile-header-updates")
        .on("postgres_changes", { event: "*", schema: "public", table: "notifications" }, () => fetchUnreadCounts())
        .on("postgres_changes", { event: "*", schema: "public", table: "messages" }, () => fetchUnreadCounts())
        .subscribe()

      return () => {
        supabase.removeChannel(channel)
      }
    }
  }, [user, loading, fetchUserProfile, fetchUnreadCounts])

  // Progress ring SVG component
  const ProgressRing = ({ percentage }: { percentage: number }) => {
    const radius = 18
    const circumference = 2 * Math.PI * radius
    const strokeDashoffset = circumference - (percentage / 100) * circumference

    return (
      <div className="relative flex items-center justify-center w-12 h-12">
        <svg className="absolute w-12 h-12 -rotate-90">
          {/* Background circle */}
          <circle
            cx="24"
            cy="24"
            r={radius}
            stroke="currentColor"
            strokeWidth="3.5"
            fill="transparent"
            className="text-slate-100 dark:text-slate-800"
          />
          {/* Progress circle */}
          <circle
            cx="24"
            cy="24"
            r={radius}
            stroke="#ff3b5c" // Pinkish color from screenshot
            strokeWidth="3.5"
            fill="transparent"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            className="transition-all duration-500 ease-in-out"
          />
        </svg>
        <Avatar className="w-8 h-8 z-10 border-2 border-white dark:border-slate-900">
          <AvatarImage src={userProfile?.avatar_url || "/placeholder.svg"} />
          <AvatarFallback className="bg-slate-200 dark:bg-slate-800 text-xs">
            {userProfile?.first_name?.charAt(0) || user?.email?.charAt(0) || 'U'}
          </AvatarFallback>
        </Avatar>
        <div className="absolute -bottom-1 bg-[#ff3b5c] text-white text-[9px] font-bold px-1 rounded-md z-20 border-2 border-white dark:border-slate-900">
          {percentage}%
        </div>
      </div>
    )
  }

  return (
    <header className="fixed top-0 left-0 right-0 z-[100] bg-white/70 dark:bg-slate-900/70 backdrop-blur-xl pt-safe border-none h-[calc(64px+env(safe-area-inset-top))]">
      <div className="h-16 px-4 flex items-center justify-between">
        <div className="flex items-center gap-1">
          {isSubPage ? (
            <button 
              onClick={handleBack}
              className="flex items-center gap-1 active:scale-95 transition-transform text-slate-900 dark:text-white"
            >
              <ChevronLeft className="w-6 h-6 stroke-[2.5]" />
              <span className="text-lg font-medium">Back</span>
            </button>
          ) : (
            <>
              {/* Profile with Progress */}
              <button 
                onClick={() => {
                  triggerHaptic('impact')
                  router.push("/profile")
                }}
                className="active:scale-95 transition-transform"
              >
                <ProgressRing percentage={completionPercentage} />
              </button>

              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  triggerHaptic('impact')
                  router.push("/explore?tab=friends")
                }}
                className="h-10 w-10 text-slate-900 dark:text-white hover:bg-transparent active:bg-slate-100 dark:active:bg-slate-800 rounded-full"
              >
                <Users className="w-6 h-6 stroke-[2.5]" />
              </Button>
            </>
          )}
        </div>

        {/* Center Content */}
        {isSubPage ? (
          <span className="text-lg font-bold tracking-tight text-slate-900 dark:text-white absolute left-1/2 -translate-x-1/2">
            {getSubPageTitle()}
          </span>
        ) : (
          <button
            onClick={() => {
              triggerHaptic('impact')
              const target = isAppMode ? "/home" : "/"
              if (pathname === target) {
                window.scrollTo({ top: 0, behavior: 'smooth' })
              } else {
                router.push(target)
              }
            }}
            className="flex items-center gap-2 active:scale-95 transition-transform absolute left-1/2 -translate-x-1/2"
          >
            <div className="w-8 h-8 relative">
              <Image 
                src="/spotstosurf-transparant.png" 
                alt="Logo" 
                fill 
                className="object-contain"
              />
            </div>
            <span className="text-xl font-black uppercase tracking-tighter italic text-slate-900 dark:text-white leading-none">
              SPOTS2SURF
            </span>
          </button>
        )}

        {/* Right Icons */}
        {!isSubPage && (
          <div className="flex items-center gap-1">
            {mounted && (
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                className="h-10 w-10 text-slate-900 dark:text-white hover:bg-transparent active:bg-slate-100 dark:active:bg-slate-800 rounded-full"
              >
                {theme === "dark" ? (
                  <Sun className="w-6 h-6 stroke-[2.5]" />
                ) : (
                  <Moon className="w-6 h-6 stroke-[2.5]" />
                )}
              </Button>
            )}

            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                triggerHaptic('impact')
                router.push("/notifications")
              }}
              className="relative h-10 w-10 text-slate-900 dark:text-white hover:bg-transparent active:bg-slate-100 dark:active:bg-slate-800 rounded-full"
            >
              <Bell className="w-6 h-6 stroke-[2.5]" />
              {unreadNotificationCount > 0 && (
                <span className="absolute top-1.5 right-1.5 min-w-[18px] h-[18px] bg-[#ff3b5c] text-white text-[10px] font-extrabold flex items-center justify-center rounded-full border-2 border-white dark:border-slate-900 px-0.5">
                  {unreadNotificationCount > 99 ? "99+" : unreadNotificationCount}
                </span>
              )}
            </Button>
          </div>
        )}
        {/* Placeholder for symmetry on subpages */}
        {isSubPage && <div className="w-16" />}
      </div>
    </header>
  )
}
